import React, { LegacyRef, MouseEventHandler, useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { WIDGET_TYPES } from '../../../util/constants';
import Button from './widgets/Button';
import { IconNames } from '../../MuiIcon/Icon';
import Image from './widgets/Image';
import Icon from './widgets/Icon';
import Text from './widgets/Text';
import Hotspot from './widgets/Hotspot';
import Iframe from './widgets/Iframe';
import Shape from './widgets/Shape';
import { ExitTiming } from '../../Player/hooks/useWidgetAnimation';

export const WIDGET_ANIMATION_TYPES = {
	[WIDGET_TYPES.WIDGET_TYPE_TEXT]: {
		entry: ['fadeIn', 'slideIn', 'zoomIn', 'slideAndFade', 'bounceIn', 'flipInX', 'wordsFade', 'wordsSlide', 'charactersFade', 'typewriter'],
		loop: ['pulse'],
		exit: ['fadeOut', 'slideOut', 'zoomOut', 'slideAndFade', 'bounceOut', 'flipOutX', 'wordsFade', 'wordsSlide', 'charactersFade', 'typewriter'],
	},
	[WIDGET_TYPES.WIDGET_TYPE_BUTTON]: {
		entry: ['fadeIn', 'slideIn', 'zoomIn', 'slideAndFade', 'bounceIn', 'flipInX', 'pillIn', 'squareIn'],
		loop: ['pulse'],
		exit: ['fadeOut', 'slideOut', 'zoomOut', 'slideAndFade', 'bounceOut', 'flipOutX', 'pillOut', 'squareOut'],
	},
	[WIDGET_TYPES.WIDGET_TYPE_SHAPE]: {
		entry: ['fadeIn', 'slideIn', 'zoomIn', 'slideAndFade', 'bounceIn', 'flipInX', 'pillIn', 'squareIn'],
		loop: ['pulse'],
		exit: ['fadeOut', 'slideOut', 'zoomOut', 'slideAndFade', 'bounceOut', 'flipOutX', 'pillOut', 'squareOut'],
	},
	[WIDGET_TYPES.WIDGET_TYPE_IMAGE]: {
		entry: ['fadeIn', 'slideIn', 'zoomIn', 'slideAndFade', 'bounceIn', 'flipInX'],
		loop: ['pulse'],
		exit: ['fadeOut', 'slideOut', 'zoomOut', 'slideAndFade', 'bounceOut', 'flipOutX'],
	},
	[WIDGET_TYPES.WIDGET_TYPE_ICON]: {
		entry: ['fadeIn', 'slideIn', 'zoomIn', 'slideAndFade', 'bounceIn', 'flipInX', 'pillIn', 'squareIn'],
		loop: ['pulse'],
		exit: ['fadeOut', 'slideOut', 'zoomOut', 'slideAndFade', 'bounceOut', 'flipOutX', 'pillOut', 'squareOut'],
	},
	[WIDGET_TYPES.WIDGET_TYPE_HOTSPOT]: {
		entry: ['fadeIn', 'slideIn', 'zoomIn', 'slideAndFade', 'bounceIn', 'flipInX', 'pillIn', 'squareIn'],
		loop: ['pulse'],
		exit: ['fadeOut', 'slideOut', 'zoomOut', 'slideAndFade', 'bounceOut', 'flipOutX', 'pillOut', 'squareOut'],
	},
};

export const WIDGET_ANIMATION_TYPE_HAS_DIRECTION = ['slideIn', 'slideOut', 'slideAndFade', "bounceIn", "bounceOut"];

export interface StyleProps {
	dimension: {
		w: number;
		h: number;
	};
	coordinate: {
		x: number;
		y: number;
	};
	rotation: number;
	zIndex: number;
	textLabel: {
		text: string;
		backgroundColor: string;
		color: string;
		fontWeight: number;
		hoverBackgroundColor: string;
		hoverFontWeight: number;
		hoverColor: string;
		font: string;
		fontSize: number;
		lineHeight: string;
		shadowColor: string;
	};
	headerLabel: {
		text: string;
		backgroundColor: string;
		color: string;
		fontWeight: number;
		hoverBackgroundColor: string;
		hoverFontWeight: number;
		hoverColor: string;
		font: string;
		fontSize: number;
		lineHeight: string;
		shadowColor: string;
	};
	transformSettings: {
		paddingBottom: string;
		paddingLeft: string;
		paddingRight: string;
		paddingTop: string;
		hoverPaddingBottom: string;
		hoverPaddingLeft: string;
		hoverPaddingRight: string;
		hoverPaddingTop: string;
		radiusBottom: string; // Use for Text widget only
		radiusLeft: string; // Use for Text widget only
		radiusRight: string; // Use for Text widget only
		radiusTop: string; // Use for Text widget only
		hoverRadiusTop: string;
		hoverRadiusRight: string;
		hoverRadiusBottom: string;
		hoverRadiusLeft: string;
		borderRadiusLeft: string;
		borderRadiusTop: string;
		borderRadiusRight: string;
		borderRadiusBottom: string;
		entryAnimation?: string;
		entryAnimationDirection?: string;
		entryAnimationDelay?: number;
		entryAnimationDuration?: number;
		exitAnimation?: string;
		exitAnimationDirection?: string;
		exitAnimationDelay?: number;
		exitAnimationDuration?: number;
		loopAnimation?: string;
		loopAnimationDelay?: number;
		loopAnimationDuration?: number;
		interactAnimation?: string;
		interactAnimationDuration?: number;
	};
	backgroundSettings: {
		backgroundColor: string;
		borderType: string;
		borderWidth: string;
		borderColor: string;
		hoverBackgroundColor: string;
		hoverBorderType: string;
		hoverBorderWidth: string;
		hoverBorderColor: string;
		backgroundOpacity: string;
		hoverBackgroundOpacity: string;
		activeBackgroundColor?: string;
	};
	iconConfig?: { position: string; size: string; spacing: string };
	textAlignment: string;
	contentSpacing: string;
}
interface WidgetInteractionBase {
	id: string;
	style: StyleProps;
	data: { type: string; start: number, settings: { iconKey?: IconNames; imgUrl: string; bgImgUrl?: string; shapeType?: string }; name: string };
	clickable: boolean;
	// Whether the widget's onClick event is a goToScene action - only that action
	// triggers the full press/collapse ("exit") animation; other actions just flash
	// the active state (see triggerClickAnimation in useWidgetAnimation).
	isGoToSceneClick?: boolean;
	scenarioId: string;
	sceneId: string;
	onClick: (_event: object) => void;
	isMobile?: boolean;
}

// The scene is being left, so every widget on screen exits at once (delay 0).
// - A clicked widget's action leaves it: the others exit with the clicked widget's timing
//   right at the click (sourceId = the clicked widget, which runs its own interact -> exit).
// - The scene's video ended: no sourceId / duration, each widget uses its own duration.
export interface GroupExit extends Partial<ExitTiming> {
	sourceId?: string;
}

const isWidgetShowingAt = (el: any, position: number) =>
	el?.data?.type !== WIDGET_TYPES.WIDGET_TYPE_IFRAME && position >= (el?.start ?? 0) && position <= (el?.end ?? 0);

// How long (ms) the exits of the widgets on screen at `position` take when they all exit
// at once with their own duration (end of scene). 0 when none has an exit animation.
export const getSceneEndExitDuration = (widgets: any[] | null | undefined, position: number): number =>
	(widgets ?? [])
		.filter((el: any) => isWidgetShowingAt(el, position) && el?.data?.style?.transformSettings?.exitAnimation)
		.reduce((longest: number, el: any) => Math.max(longest, el.data.style.transformSettings.exitAnimationDuration ?? 1000), 0);

interface WidgetInteractionItem extends WidgetInteractionBase {
	start?: number;
	end?: number;
	position?: number;
	onEnter?: () => void;
	onExit?: () => void;
	onExitAnimationComplete?: (_id: string) => void;
	groupExit?: GroupExit | null;
	onClickExitStart?: (_timing: ExitTiming) => void;
}

export interface DetailWidgetInteractionItem extends WidgetInteractionBase {
	isHover: boolean;
	widgetRef: LegacyRef<SVGForeignObjectElement>;
	handleMouseEnter?: MouseEventHandler<HTMLDivElement> | MouseEventHandler<SVGImageElement> | MouseEventHandler<SVGGElement>;
	handleMouseLeave?: MouseEventHandler<HTMLDivElement> | MouseEventHandler<SVGImageElement> | MouseEventHandler<SVGGElement>;
	position?: number;
	onEnter?: () => void;
	onExit?: () => void;
	isShowWidget?: boolean;
	isExiting?: boolean;
	onExitDone?: () => void;
	exitTiming?: ExitTiming;
	onClickExitStart?: (_timing: ExitTiming) => void;
}

const createWidget = (props: DetailWidgetInteractionItem) => {
	const {
		data: { type },
	} = props;

	switch (type) {
		case WIDGET_TYPES.WIDGET_TYPE_BUTTON:
			return <Button {...props} key={props.id} />;
		case WIDGET_TYPES.WIDGET_TYPE_SHAPE:
			return <Shape {...props} key={props.id} />;
		case WIDGET_TYPES.WIDGET_TYPE_IMAGE:
			return <Image {...props} key={props.id} />;
		case WIDGET_TYPES.WIDGET_TYPE_ICON:
			return <Icon {...props} key={props.id} />;
		case WIDGET_TYPES.WIDGET_TYPE_TEXT:
			return <Text {...props} key={props.id} />;
		case WIDGET_TYPES.WIDGET_TYPE_HOTSPOT:
			return <Hotspot {...props} key={props.id} />;
		case WIDGET_TYPES.WIDGET_TYPE_IFRAME:
			return <Iframe {...props} key={props.id} />;
		default:
			return <></>;
	}
};

const WidgetElement = ({
	id,
	style,
	data,
	clickable,
	isGoToSceneClick,
	end,
	start,
	position,
	scenarioId,
	sceneId,
	isMobile,
	onClick,
	onEnter,
	onExit,
	onExitAnimationComplete,
	groupExit,
	onClickExitStart,
}: WidgetInteractionItem) => {
	const [isExiting, setIsExiting] = useState(false);
	const [isHover, setIsHover] = useState(false);
	const [isTriggeredOnExitEvent, setTriggeredOnExitEvent] = useState(false);
	const widgetRef = useRef<SVGForeignObjectElement>(null);

	const isShowWidget = (position ?? 0) >= (start ?? 0) && (position ?? 0) <= (end ?? 0);
	const exitAnimation = style?.transformSettings?.exitAnimation;
	const exitAnimationDuration = style?.transformSettings?.exitAnimationDuration ?? 1000;

	// another widget was clicked and its action leaves the scene -> exit right away.
	// Latched until groupExit clears: if the playhead then passes the widget's end, isShowWidget
	// drops, and without the latch the exit would switch to the widget's own timing - replaying
	// it from the start, so the widget shows again and plays its out a second time.
	const groupExitTimingRef = useRef<ExitTiming | undefined>(undefined);
	if (!groupExit) {
		groupExitTimingRef.current = undefined;
	} else if (
		!groupExitTimingRef.current &&
		groupExit.sourceId !== id && exitAnimation && isShowWidget && data.type !== WIDGET_TYPES.WIDGET_TYPE_IFRAME
	) {
		groupExitTimingRef.current = { duration: groupExit.duration ?? exitAnimationDuration, delay: groupExit.delay ?? 0 };
	}
	const groupExitTiming = groupExitTimingRef.current;
	const isGroupExiting = Boolean(groupExitTiming);

	const prevIsShowWidget = useRef(isShowWidget);

	const handleMouseEnter = isMobile
		? undefined
		: () => {
			setIsHover(true);
		};

	const handleMouseLeave = isMobile
		? undefined
		: () => {
			setIsHover(false);
		};

	useEffect(() => {
		const wasShowing = prevIsShowWidget.current;
		prevIsShowWidget.current = isShowWidget;

		if (!isShowWidget && wasShowing && data.type !== WIDGET_TYPES.WIDGET_TYPE_IFRAME) {
			if (exitAnimation) {
				setIsExiting(true);
			}
		} else if (isShowWidget && !wasShowing) {
			setIsExiting(false);
		}
	}, [isShowWidget, exitAnimation, data.type]);

	const handleExitDone = useCallback(() => {
		setIsExiting(false);
		onExitAnimationComplete?.(id);
	}, [onExitAnimationComplete, id]);

	useLayoutEffect(() => {
		onEnter?.();
	}, []);

	useEffect(() => {
		if (end !== undefined && position !== undefined && end - position <= 1 && !isTriggeredOnExitEvent) {
			onExit?.();
			setTriggeredOnExitEvent(true);
		}
	}, [position, end, isTriggeredOnExitEvent]);

	if (!isShowWidget && (position ?? 0) < data.start) {
		return null;
	}

	return createWidget({
		id,
		data,
		style,
		clickable,
		isGoToSceneClick,
		isHover,
		widgetRef,
		scenarioId,
		sceneId,
		onClick,
		position,
		handleMouseEnter,
		handleMouseLeave,
		isShowWidget,
		isExiting: isExiting || isGroupExiting,
		onExitDone: handleExitDone,
		exitTiming: groupExitTiming,
		onClickExitStart,
	});
};

export default WidgetElement;
